import type { ComponentType, ReactNode } from "react";
import { useMemo, useRef } from "react";
import { Box } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ImageRP from "@/components/ImageRP";
import { isNationalPlusUpCampaign } from "@/features/campaigns/direct-mail/national-plus-up/utils/nationalPlusUpHelpers";
import type { DirectMailCampaignCardData } from "./directMailCampaignCardTypes";

type CampaignSliderImage = {
  src: string;
  alt: string;
  id?: string | number;
  type?: string;
};

type SliderInstance = {
  slickPrev: () => void;
  slickNext: () => void;
};

type CampaignImageSliderProps = {
  campaign?: DirectMailCampaignCardData;
  isLoading?: boolean;
};

type CustomArrowProps = {
  direction: "prev" | "next";
  onClick: () => void;
  icon: ReactNode;
};

const SlickSlider = Slider as ComponentType<
  Record<string, unknown> & { ref?: React.Ref<SliderInstance> }
>;

const PLACEHOLDER_IMAGE = [
  {
    src: "/img/hyper_placeholder.png",
    alt: "Default campaign image",
    id: "default",
    type: "HYPER_PLACEHOLDER",
  },
];

const sliderSx = {
  "& .campaign-image, & .campaign-image-slide": {
    width: "100%",
    display: "block",
    borderRadius: "var(--radius-container)",
    height: "auto",
    maxHeight: 220,
    objectFit: "cover",
    aspectRatio: "14/9",
  },
  "& .campaign-image-slider-container": {
    position: "relative",
    width: "100%",
  },
  "& .campaign-image-slider": {
    overflow: "hidden",
    position: "relative",
    "& .slick-slide": {
      padding: "0 15px",
      "&:not(.slick-center)": {
        opacity: 0.9,
        "& .campaign-image-wrapper": {
          position: "relative",
          objectFit: "cover",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "color-mix(in srgb, var(--palette-common-black) 60%, transparent)",
            borderRadius: "var(--radius-container)",
          },
        },
      },
    },
  },
  "& .campaign-custom-arrows-wrapper": {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    zIndex: 10,
    "@media (max-width: 1000px)": { display: "none !important" },
    "& .campaign-slider-arrow": {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 35,
      height: 40,
      borderRadius: "var(--radius-control)",
      background: "color-mix(in srgb, var(--neutral-white) 18%, transparent)",
      backdropFilter: "blur(3px)",
      cursor: "pointer",
      border: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "auto",
      zIndex: 10,
      "@media (max-width: 1500px)": {
        backdropFilter: "none",
        background: "transparent",
      },
      "& svg": { color: "var(--neutral-white)" },
      "&.campaign-slider-arrow-prev": {
        left: "clamp(15px, 0vw, 20px)",
        "@media (max-width: 1700px)": { left: "5px" },
      },
      "&.campaign-slider-arrow-next": {
        right: "clamp(15px, 0vw, 20px)",
        "@media (max-width: 1700px)": { right: "5px" },
      },
    },
  },
};

const buildImages = (
  campaign?: DirectMailCampaignCardData
): CampaignSliderImage[] => {
  if (isNationalPlusUpCampaign(campaign) && campaign?.hero_image) {
    return [
      {
        src: campaign.hero_image,
        alt: "National Plus Up campaign image",
        id: "national-plus-up-hero",
        type: "CREATIVE",
      },
    ];
  }

  const imageSource = campaign?.creative_data || campaign?.banner_image;
  if (Array.isArray(imageSource) && imageSource.length > 0) {
    const filtered = imageSource
      .filter((c) => c?.attachment)
      .map((c) => ({
        src: c.attachment,
        alt: c.creative_name || "",
        id: c.id || c.creative_id,
        type: "CREATIVE",
      }));
    if (filtered.length > 0) return filtered;
  }
  return PLACEHOLDER_IMAGE;
};

const buildSliderSettings = (imageCount: number) => ({
  infinite: imageCount > 1,
  slidesToShow: 1,
  slidesToScroll: 1,
  speed: 500,
  arrows: true,
  dots: false,
  adaptiveHeight: false,
  centerMode: true,
  centerPadding: "15%",
  swipe: true,
  touchMove: true,
  responsive: [
    {
      breakpoint: 576,
      settings: {
        arrows: false,
        centerMode: imageCount > 1,
        centerPadding: imageCount > 1 ? "15%" : "0px",
      },
    },
    {
      breakpoint: 0,
      settings: {
        arrows: false,
        centerMode: false,
        centerPadding: "0px",
        swipe: true,
        touchMove: true,
      },
    },
  ],
});

const CustomArrow = ({ direction, onClick, icon }: CustomArrowProps) => (
  <button
    onClick={onClick}
    className={`campaign-slider-arrow campaign-slider-arrow-${direction}`}
    aria-label={direction === "prev" ? "Previous slide" : "Next slide"}
  >
    {icon}
  </button>
);

const CampaignImageSlider = ({
  campaign,
  isLoading,
}: CampaignImageSliderProps) => {
  const sliderRef = useRef<SliderInstance | null>(null);

  const images = useMemo(() => buildImages(campaign), [campaign]);

  const sliderSettings = useMemo(
    () => buildSliderSettings(images.length),
    [images.length]
  );

  const hasMultipleImages = images.length > 1;

  return (
    <Box sx={sliderSx}>
      <div className="campaign-image-slider-container">
        <div className="campaign-image-slider">
          <SlickSlider ref={sliderRef} {...sliderSettings}>
            {!isLoading &&
              images.map((image) => (
                <div key={image.id}>
                  <div
                    className={
                      hasMultipleImages
                        ? "campaign-image-wrapper"
                        : "d-flex justify-content-center"
                    }
                  >
                    <ImageRP
                      src={image.src}
                      type={image.type || "CREATIVE"}
                      alt={image.alt}
                      className={
                        hasMultipleImages
                          ? "img-fluid campaign-image-slide"
                          : "img-fluid rounded campaign-image"
                      }
                      style={
                        !hasMultipleImages
                          ? {
                              maxHeight: 220,
                              objectFit:
                                image.type !== "IMG" ? "contain" : "cover",
                            }
                          : {}
                      }
                    />
                  </div>
                </div>
              ))}
          </SlickSlider>
          {!isLoading && hasMultipleImages && (
            <div className="campaign-custom-arrows-wrapper">
              <CustomArrow
                direction="prev"
                onClick={() => sliderRef.current?.slickPrev()}
                icon={<ArrowBackIosIcon fontSize="small" className="ml-2" />}
              />
              <CustomArrow
                direction="next"
                onClick={() => sliderRef.current?.slickNext()}
                icon={<ArrowForwardIosIcon fontSize="small" className="ml-1" />}
              />
            </div>
          )}
        </div>
      </div>
    </Box>
  );
};

export default CampaignImageSlider;
